import React, { useState } from 'react';
import { Alert, Button, DatePicker, Input, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import useRetakeStatistics, { EmployeeRetakes, RetakeAttempt } from '../hooks/useRetakeStatistics';
import type { StatisticsPeriod } from '../hooks/useUserStatisticsPeriod';
import { formatTime } from '../utils/formatTime';
import './EmployeeRetakeStatistics.css';

dayjs.extend(utc);
dayjs.extend(timezone);
const scoreText = (value: number) => `${Number(value.toFixed(2))}%`;
const moscowDate = (value: string) => dayjs.utc(value).tz('Europe/Moscow').format('DD.MM.YYYY HH:mm');

export default function EmployeeRetakeStatistics() {
  const stats = useRetakeStatistics();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<React.Key[]>([]);
  const changePeriod = (period: StatisticsPeriod) => {
    stats.changePeriod(period);
    setPage(1);
    setExpanded([]);
  };
  const range = stats.period
    ? [dayjs(stats.period.from), dayjs(stats.period.to)] as [dayjs.Dayjs, dayjs.Dayjs] : null;
  const month = range && range[0].isSame(range[0].startOf('month'), 'day')
    && range[1].isSame(range[0].endOf('month'), 'day') ? range[0] : null;
  const needle = search.trim().toLocaleLowerCase('ru');
  const users = stats.users.filter(user => `${user.full_name} ${user.department}`.toLocaleLowerCase('ru').includes(needle));

  return <section className="employee-retake-statistics" aria-labelledby="employee-retakes-title">
    <Typography.Title level={4} id="employee-retakes-title">Пересдачи по сотрудникам ({stats.users.length})</Typography.Title>
    <div className="employee-retake-statistics__filters">
      <label>Месяц <DatePicker picker="month" format="MM.YYYY" placeholder="Выберите месяц"
        aria-label="Месяц пересдач" value={month} onChange={value => changePeriod(value ? {
          from: value.startOf('month').format('YYYY-MM-DD'), to: value.endOf('month').format('YYYY-MM-DD'),
        } : null)} /></label>
      <label>Период <DatePicker.RangePicker format="DD.MM.YYYY" value={range}
        placeholder={['Пересдачи с даты', 'Пересдачи по дату']}
        onChange={values => changePeriod(values?.[0] && values?.[1] ? {
          from: values[0].format('YYYY-MM-DD'), to: values[1].format('YYYY-MM-DD'),
        } : null)} /></label>
      <Button disabled={!stats.period} onClick={() => changePeriod(null)}>За всё время</Button>
      <Button onClick={() => changePeriod(stats.period)} loading={stats.loading}>Обновить пересдачи</Button>
      <Input.Search allowClear placeholder="Сотрудник или отдел" aria-label="Найти сотрудника в пересдачах"
        value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} />
    </div>
    <Typography.Paragraph type="secondary">
      {range ? `${range[0].format('DD.MM.YYYY')} — ${range[1].format('DD.MM.YYYY')}` : 'За всё время'}.
      {' '}Вторая и последующие завершённые попытки того же теста. Период — по дате завершения пересдачи (МСК).
      {' '}Средний балл — по всем пересдачам периода; первая попытка берётся из полной истории.
    </Typography.Paragraph>
    {stats.error && <Alert type="error" showIcon message={stats.error}
      action={<Button onClick={() => changePeriod(stats.period)}>Повторить</Button>} />}
    {!stats.error && <Table<EmployeeRetakes> size="small" rowKey="user_id" loading={stats.loading}
      dataSource={users} scroll={{ x: 1000 }}
      locale={{ emptyText: needle ? 'Сотрудник не найден' : 'За выбранный период пересдач нет' }}
      pagination={{ current: page, pageSize: 20, showSizeChanger: false, onChange: setPage,
        showTotal: total => `Сотрудников с пересдачами: ${total}` }}
      columns={[
        { title: 'Сотрудник', dataIndex: 'full_name', width: 220 },
        { title: 'Отдел', dataIndex: 'department', width: 220 },
        { title: 'Пересдач', dataIndex: 'retakes_count', width: 100,
          sorter: (a, b) => a.retakes_count - b.retakes_count },
        { title: 'Тестов', dataIndex: 'tests_count', width: 80 },
        { title: 'Сдано', dataIndex: 'passed_count', width: 90, render: value => <Tag color="success">{value}</Tag> },
        { title: 'Не сдано', dataIndex: 'failed_count', width: 100, render: value => <Tag color="error">{value}</Tag> },
        { title: 'Средний балл', dataIndex: 'average_score', width: 120,
          sorter: (a, b) => a.average_score - b.average_score, render: scoreText },
        { title: 'Последняя пересдача (МСК)', dataIndex: 'latest_completed_at', width: 180, render: moscowDate },
      ]}
      expandable={{ expandedRowKeys: expanded, onExpandedRowsChange: keys => setExpanded([...keys]),
        expandedRowRender: employee => <Table<RetakeAttempt> size="small" rowKey="attempt_id"
          dataSource={employee.attempts} pagination={false} scroll={{ x: 1000 }}
          columns={[
            { title: 'Тест', dataIndex: 'test_title', width: 240 },
            { title: 'Первая попытка (история)', width: 200, render: (_, attempt) => <div>
              <strong>{scoreText(attempt.first_score)}</strong><br />{moscowDate(attempt.first_completed_at)}
            </div> },
            { title: 'Пересдача', dataIndex: 'retake_number', width: 100,
              render: (value, attempt) => <span title={`Попытка сотрудника №${attempt.attempt_number}`}>№{value}</span> },
            { title: 'Дата завершения (МСК)', dataIndex: 'completed_at', width: 180, render: moscowDate },
            { title: 'Результат', dataIndex: 'score', width: 100, render: scoreText },
            { title: 'Статус', dataIndex: 'is_passed', width: 100,
              render: passed => <Tag color={passed ? 'success' : 'error'}>{passed ? 'Сдан' : 'Не сдан'}</Tag> },
            { title: 'Время', dataIndex: 'time_spent_seconds', width: 100, render: formatTime },
          ]} /> }} />}
  </section>;
}
